import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";

import store from "@/store";
import RefService from "@/services/ReferensiAsuhanKeperawatan";
import AsuhanKeperawatanGawatDarurat from "@/services/AsuhanKeperawatanGawatDarurat";

interface RefData {
  id: any;
  name: string;
}

// Ref Askep (System A) master is nested: each diagnosis carries its own
// luaran / intervensi(.item). IGD derives its flat pick-lists client-side
// from that single fetch — mirroring AskepRanapInputModal, no mapping endpoint.
function dedupeById(arr: any[]): any[] {
  const seen: Record<string, boolean> = {};
  const out: any[] = [];
  (arr || []).forEach(it => {
    if (it && it.id != null && !seen[it.id]) {
      seen[it.id] = true;
      out.push(it);
    }
  });
  return out;
}

function deriveTujuan(diag: any[]): RefData[] {
  return dedupeById(
    (diag || []).reduce((a, d) => a.concat(d.luaran || []), [])
  ).map(l => ({ id: l.id, name: l.name }));
}

function deriveRencana(diag: any[]): RefData[] {
  return dedupeById(
    (diag || []).reduce((a, d) => a.concat(d.intervensi || []), [])
  ).map(i => ({ id: i.id, name: i.name }));
}

function deriveImplementasi(diag: any[]): RefData[] {
  return dedupeById(
    (diag || []).reduce(
      (a, d) =>
        a.concat((d.intervensi || []).reduce((b, i) => b.concat(i.item || []), [])),
      []
    )
  ).map(it => ({ id: it.id, name: it.name || it.implementasi }));
}

@Module({
  dynamic: true,
  namespaced: true,
  store,
  name: "asuhan-keperawatan-gadar"
})
class AsuhanKeperawatanGadarStore extends VuexModule {
  listDiag: RefData[] = [];
  listTujuan: RefData[] = [];
  listRencana: RefData[] = [];
  listImplementasi: RefData[] = [];
  listAsKep = [];

  get isAllRefExist(): boolean {
    return (
      this.listDiag.length > 0 &&
      this.listTujuan.length > 0 &&
      this.listRencana.length > 0 &&
      this.listImplementasi.length > 0
    );
  }

  @Mutation
  SET_LIST_DIAG(val: RefData[]) {
    this.listDiag = val;
  }

  @Mutation
  SET_LIST_TUJUAN(val: RefData[]) {
    this.listTujuan = val;
  }

  @Mutation
  SET_LIST_RENCANA(val: RefData[]) {
    this.listRencana = val;
  }

  @Mutation
  SET_LIST_IMPLEMENTASI(val: RefData[]) {
    this.listImplementasi = val;
  }

  @Mutation
  SET_LIST_ASKEP(val: []) {
    this.listAsKep = val;
  }

  @Action({ rawError: true })
  async getListDiagnosis(): Promise<void> {
    const response = await RefService.getDiagnosisCached();
    const diag = response.data || [];

    this.SET_LIST_DIAG(diag);
    this.SET_LIST_TUJUAN(deriveTujuan(diag));
    this.SET_LIST_RENCANA(deriveRencana(diag));
    this.SET_LIST_IMPLEMENTASI(deriveImplementasi(diag));
  }

  @Action({ rawError: true })
  async getListTujuan(): Promise<void> {
    if (this.listDiag.length === 0) {
      await this.getListDiagnosis();
      return;
    }
    this.SET_LIST_TUJUAN(deriveTujuan(this.listDiag as any[]));
  }

  @Action({ rawError: true })
  async getListRencana(): Promise<void> {
    if (this.listDiag.length === 0) {
      await this.getListDiagnosis();
      return;
    }
    this.SET_LIST_RENCANA(deriveRencana(this.listDiag as any[]));
  }

  @Action({ rawError: true })
  async getListImplementasi(): Promise<void> {
    if (this.listDiag.length === 0) {
      await this.getListDiagnosis();
      return;
    }
    this.SET_LIST_IMPLEMENTASI(deriveImplementasi(this.listDiag as any[]));
  }

  @Action({ rawError: true })
  async getAllRefList(force = false): Promise<void> {
    if (!this.isAllRefExist || force) {
      await this.getListDiagnosis();
    }
  }

  @Action({ rawError: true })
  async getListAskep(register): Promise<void> {
    const response = await AsuhanKeperawatanGawatDarurat.getByRegister(
      register
    );
    this.SET_LIST_ASKEP(response.data);
  }
  @Action({ rawError: true })
  async clearListAskep(): Promise<void> {
    this.SET_LIST_ASKEP([]);
  }
}

export default getModule(AsuhanKeperawatanGadarStore);
